import api from './api';
import { herunterladen, pdfOeffnen } from './download';
import type { Invoice, InvoiceItem, Dunning, OffenePosten, OffenePostenFirma, Payment } from './erpTypes';

export const RECHNUNG_STATUS_LABEL: Record<string, string> = {
  draft: 'Entwurf',
  issued: 'Ausgestellt',
  sent: 'Versendet',
  partially_paid: 'Teilbezahlt',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
  cancelled: 'Storniert',
};

export const RECHNUNG_ART_LABEL: Record<string, string> = {
  rechnung: 'Rechnung',
  gutschrift: 'Gutschrift',
};

export const STEUERART_LABEL: Record<string, string> = {
  regel: 'Regelbesteuerung',
  reverse_charge: 'Reverse Charge (§13b UStG)',
  innergemeinschaftlich: 'Innergemeinschaftliche Lieferung',
  kleinunternehmer: 'Kleinunternehmer (§19 UStG)',
};

export const ZAHLUNGSMETHODE_LABEL: Record<string, string> = {
  ueberweisung: 'Überweisung',
  lastschrift: 'Lastschrift',
  bar: 'Bar',
  karte: 'Karte',
  verrechnung: 'Verrechnung',
};

export type RechnungDaten = Partial<Pick<Invoice,
  'companyId' | 'dealId' | 'orderId' | 'projectId' | 'art' | 'steuerart' | 'discountPercent' | 'introText' | 'termsText'
  | 'leistungsdatum' | 'leistungszeitraumVon' | 'leistungszeitraumBis' | 'dueAt' | 'leitwegId' | 'bestellreferenz'
  | 'empfaengerName' | 'empfaengerAdresse' | 'empfaengerUstId'>>;

export type PositionDaten = Partial<Pick<InvoiceItem,
  'description' | 'unit' | 'quantityHundert' | 'unitPriceCents' | 'discountPercent' | 'taxRate' | 'position'>> & { productId?: string };

export interface ZahlungDaten {
  amountCents: number;
  bookedAt?: string;
  methode?: string;
  verwendungszweck?: string;
  referenz?: string;
}

/** Aus dem Ausstellen-400 die Maengelliste ziehen (Pflichtangaben §14 UStG). */
export const maengelAus = (e: any): string[] => {
  const m = e?.response?.data?.maengel;
  return Array.isArray(m) ? m.map(String) : [];
};

const rechnungService = {
  async list(filter?: {
    status?: string; art?: string; companyId?: string; projectId?: string; faelligBis?: string; ueberfaellig?: boolean;
  }): Promise<Invoice[]> {
    const response = await api.get('/rechnungen', { params: filter || {} });
    return response.data;
  },
  async offenePosten(): Promise<OffenePosten> {
    const response = await api.get('/rechnungen/offene-posten');
    return response.data;
  },
  /** Nur die Summe einer Firma (erp.offene_posten.lesen), ohne Rechnungsliste. */
  async offenePostenFirma(companyId: string): Promise<OffenePostenFirma> {
    const response = await api.get(`/rechnungen/offene-posten/firma/${encodeURIComponent(companyId)}`);
    return response.data;
  },
  async get(id: string): Promise<Invoice> {
    const response = await api.get(`/rechnungen/${id}`);
    return response.data;
  },
  async create(daten: RechnungDaten): Promise<Invoice> {
    const response = await api.post('/rechnungen', daten);
    return response.data;
  },
  async ausZeiten(daten: { projectId: string; bis?: string; nurAbrechenbar?: boolean }): Promise<Invoice> {
    const response = await api.post('/rechnungen/aus-zeiten', daten);
    return response.data;
  },
  async update(id: string, daten: RechnungDaten): Promise<Invoice> {
    const response = await api.patch(`/rechnungen/${id}`, daten);
    return response.data;
  },
  async addItem(id: string, daten: PositionDaten): Promise<Invoice> {
    const response = await api.post(`/rechnungen/${id}/positionen`, daten);
    return response.data;
  },
  async updateItem(itemId: string, daten: PositionDaten): Promise<Invoice> {
    const response = await api.patch(`/rechnungen/positionen/${itemId}`, daten);
    return response.data;
  },
  async removeItem(itemId: string): Promise<Invoice> {
    const response = await api.delete(`/rechnungen/positionen/${itemId}`);
    return response.data;
  },
  /** Ab hier ist der Beleg unveraenderlich; bei 400 traegt die Antwort `maengel`. */
  async ausstellen(id: string): Promise<Invoice> {
    const response = await api.post(`/rechnungen/${id}/ausstellen`);
    return response.data;
  },
  async setStatus(id: string, status: 'sent'): Promise<Invoice> {
    const response = await api.post(`/rechnungen/${id}/status`, { status });
    return response.data;
  },
  /** Liefert die neue Gutschrift; das Original wird cancelled. */
  async storno(id: string, grund?: string): Promise<Invoice> {
    const response = await api.post(`/rechnungen/${id}/storno`, { grund });
    return response.data;
  },
  async zahlungBuchen(id: string, daten: ZahlungDaten): Promise<Invoice> {
    const response = await api.post(`/rechnungen/${id}/zahlung`, daten);
    return response.data;
  },
  async mahnungen(id: string): Promise<Dunning[]> {
    const response = await api.get(`/rechnungen/${id}/mahnungen`);
    return response.data;
  },
  /** Was dem Entwurf zum Ausstellen noch fehlt -- dieselbe Liste wie im 400 von `ausstellen`. */
  async maengel(id: string): Promise<string[]> {
    const response = await api.get(`/rechnungen/${id}/maengel`);
    const m = response.data?.maengel;
    return Array.isArray(m) ? m.map(String) : [];
  },
  pdfUrl: (id: string) => `/rechnungen/${id}/pdf`,
  pdfOeffnen: (id: string) => pdfOeffnen(`/rechnungen/${id}/pdf`),
  xrechnungHerunterladen: (id: string, nummer?: string | null) =>
    herunterladen(`/rechnungen/${id}/xrechnung.xml`, `${nummer || id}-xrechnung.xml`),
  zugferdHerunterladen: (id: string, nummer?: string | null) =>
    herunterladen(`/rechnungen/${id}/zugferd.pdf`, `${nummer || id}-zugferd.pdf`),
};

export type { Payment };
export default rechnungService;
